import React from 'react';
import {
  getHeroContent, HeroImage, TabContent, StoreFooter, getTabLabel, InlineEditableText, MdFilterList,
  StoreLogo, SearchBar, CartBtn
} from './ThemeLayoutHelpers';

export default function SolarLayout({ p }) {
  const T = p.storeTheme || {};
  const text = p.storefrontText || {};
 
  const copy = getHeroContent(p, {
    eyebrow: "Radiant living",
    title: "Brighten your world",
    subtitle: "Energetic products that bring joy and warmth to everyday life.",
    primaryCta: "Discover",
    secondaryCta: "Learn More",
  });
  
  const categories = p.allProducts?.reduce((acc, product) => {
    const cat = product.category || "Uncategorized";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(product);
    return acc;
  }, {}) || {};

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50" style={{ fontFamily: T.fontFamily || "Poppins, sans-serif" }}>
      {/* Navigation Header */}
      <nav className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-orange-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <StoreLogo storeInfo={p.storeInfo} size="h-9 w-9" border="border border-orange-200" />
            <span className="font-bold text-base tracking-tight bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">
              {p.storeInfo?.businessName}
            </span>
          </div>
          
          <div className="flex items-center gap-3">
            <SearchBar searchQuery={p.searchQuery} setSearchQuery={p.setSearchQuery} theme={T} className="hidden md:block w-64" />
            <CartBtn p={p} />
            {p.userMenuElement}
          </div>
        </div>
      </nav>
      {/* Hero Banner */}
      <div className="bg-gradient-to-r from-orange-600 to-amber-600 text-white">
        <div className="max-w-7xl mx-auto px-4 py-16">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <span className="inline-flex items-center gap-2 bg-white/20 rounded-full px-3 py-1 text-sm mb-4">
                <span className="w-2 h-2 rounded-full bg-yellow-300 animate-pulse" /><InlineEditableText p={p} field="heroEyebrow" value={copy.eyebrow}>{copy.eyebrow}</InlineEditableText></span>
              <h1 className="text-5xl font-bold leading-tight"><InlineEditableText p={p} field="heroTitle" value={copy.title}>{copy.title}</InlineEditableText></h1>
              <p className="mt-4 text-orange-100 text-lg"><InlineEditableText p={p} field="heroSubtitle" value={copy.subtitle}>{copy.subtitle}</InlineEditableText></p>
              <div className="mt-8 flex gap-3">
                <button onClick={() => p.setActiveTab("products")} className="px-8 py-3 rounded-full bg-white text-orange-600 font-bold hover:shadow-lg transition"><InlineEditableText p={p} field="primaryCtaText" value={copy.primaryCta}>{copy.primaryCta}</InlineEditableText></button>
                <button onClick={() => p.setActiveTab("details")} className="px-8 py-3 rounded-full border border-white/30 text-white font-semibold hover:bg-white/10 transition"><InlineEditableText p={p} field="secondaryCtaText" value={copy.secondaryCta}>{copy.secondaryCta}</InlineEditableText></button>
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-orange-500 to-amber-500 rounded-full blur-3xl opacity-30" />
              <HeroImage p={p} themeId="solar" className="h-64 rounded-2xl relative z-10" />
            </div>
          </div>
        </div>
      </div>

      {/* Category Strip */}
      <div className="bg-white shadow-sm sticky top-[61px] z-30">
        <div className="max-w-7xl mx-auto px-4 overflow-x-auto">
          <div className="flex gap-1 py-3 min-w-max">
            {Object.keys(categories).slice(0, 6).map(cat => (
              <button key={cat} className="px-4 py-2 rounded-full text-sm font-medium text-gray-600 hover:bg-orange-50 hover:text-orange-600 transition whitespace-nowrap">{cat}</button>
            ))}
            <button onClick={() => p.setFilterPanelOpen(true)} className="px-4 py-2 rounded-full bg-orange-100 text-orange-600 font-medium flex items-center gap-1">
              <MdFilterList /> All
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto w-full px-4 py-8">
        <div className="flex gap-2 mb-6 border-b border-orange-200">
          {[["products", getTabLabel(p, "products")], ["services", getTabLabel(p, "services")], ["details", getTabLabel(p, "details")]].map(([k, l]) => (
            <button key={k} onClick={() => p.setActiveTab(k)} className={`px-6 py-3 text-sm font-semibold transition relative ${p.activeTab === k ? "text-orange-600" : "text-gray-500"}`}>
              {l}
              {p.activeTab === k && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-orange-500 rounded-full" />}
            </button>
          ))}
        </div>
        <TabContent p={p} />
      </div>

      {/* Features Banner */}
      <div className="bg-orange-100 mt-8 py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { icon: "🚚", label: "Free Shipping", desc: "On orders above ₹999" },
              { icon: "🔄", label: "Easy Returns", desc: "30-day return policy" },
              { icon: "🔒", label: "Secure Payment", desc: "100% safe checkout" },
              { icon: "💬", label: "24/7 Support", desc: "Always here to help" },
            ].map(f => (
              <div key={f.label}>
                <div className="text-3xl mb-2">{f.icon}</div>
              <div className="font-semibold text-orange-800">{f.label}</div>
              <div className="text-xs text-orange-600">{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <StoreFooter p={p} />
    </div>
  );
}